import { __rest } from "tslib";
import React, { forwardRef } from 'react';
import { makeStyles, Box, Button as MuiButton, CircularProgress, } from '@material-ui/core';
import clsx from 'clsx';
import { reactStyling, tokens } from '@getgo/chameleon-core';
const useStyles = makeStyles((theme) => {
    const { button, buttonPrimary, buttonPrimaryActive, buttonDanger, buttonDangerHover, buttonDangerActive, buttonDangerFocus, buttonSuccess, buttonSuccessHover, buttonSuccessFocus, buttonSuccessActive, buttonSecondary, buttonSecondaryHover, buttonSecondaryActive, buttonTertiary, buttonTertiaryActive, buttonTertiaryHover, buttonTertiaryFocus, buttonTertiaryNeutral, buttonTertiaryNeutralActive, buttonTertiaryNeutralHover, buttonNeutral, buttonNeutralActive, buttonNeutralHover, buttonNeutralInverse, buttonNeutralInverseHover, buttonNeutralInverseActive, buttonNeutralInverseFocus, buttonDisabled, buttonDisabledHover, buttonDisabledSecondary, buttonDisabledTertiary, } = reactStyling(theme.tokens);
    const normalizedButton = Object.assign(Object.assign({}, button), { width: undefined, height: undefined, padding: undefined, font: undefined });
    const defaultProps = {
        inherit: {},
        secondary: {},
    };
    // Styles for primary and default button colors
    const defaultStyles = {
        '&:not(.Mui-disabled)': Object.assign(Object.assign(Object.assign({}, normalizedButton), buttonPrimary), { backgroundColor: tokens.interactive01, borderColor: tokens.interactive01, '&:hover': {
                backgroundColor: tokens.hoverPrimary,
                borderColor: tokens.hoverPrimary,
            }, '&.Mui-focusVisible': {
                boxShadow: tokens.highlight,
                border: tokens.inverseInteractive01,
            }, '&:active': Object.assign(Object.assign({}, buttonPrimaryActive), { backgroundColor: tokens.pressedPrimary, borderColor: tokens.pressedPrimary }) }),
    };
    return Object.assign(Object.assign({}, defaultProps), { '&:hover': {
            backgroundColor: tokens.inverseInteractive01,
            borderColor: tokens.inverseInteractive01,
        }, contained: {
            '&:disabled': Object.assign(Object.assign({}, buttonDisabled), { minWidth: 80, '&:hover': Object.assign({}, buttonDisabledHover) }),
            '&:not(.Mui-disabled)': Object.assign(Object.assign(Object.assign({}, normalizedButton), buttonSecondary), { color: tokens.interactive01, backgroundColor: tokens.interactive02, '&:hover': Object.assign(Object.assign({}, buttonSecondaryHover), { backgroundColor: tokens.hoverSecondary }), '&:active': Object.assign(Object.assign({}, buttonSecondaryActive), { backgroundColor: tokens.pressedSecondary }) }),
        }, 
        // Allowing !important as we need to create a separate component for the IconButton (https://share.goabstract.com/bf142bf4-191b-4b58-a698-c3da45909ff3?sha=f31a5eca79192a1588b5e47cae567801cdf2116b)
        iconButton: {
            width: 40,
            minWidth: '40px!important',
            padding: '5px!important',
        }, small: {
            padding: '3px 12px 3px 12px',
            font: tokens.buttonTextSmall,
            height: 30,
        }, medium: {
            padding: '0 16px',
            font: tokens.buttonTextMedium,
            height: 36,
        }, large: {
            padding: '8px 20px 8px 20px',
            font: tokens.buttonTextLarge,
            height: 42,
        }, xlarge: {
            padding: '11px 24px 11px 24px',
            font: tokens.buttonTextXlarge,
            height: 48,
        }, focusVisible: {
            opacity: '1 !important',
            boxShadow: tokens.highlight,
            border: tokens.inverseInteractive01,
        }, primary: defaultStyles, default: defaultStyles, outlined: {
            '&:disabled': Object.assign(Object.assign({}, buttonDisabledSecondary), { minWidth: 80 }),
            '&:not(.Mui-disabled)': Object.assign(Object.assign(Object.assign({}, normalizedButton), buttonSecondary), { color: tokens.inverseInteractive01, '&:hover': Object.assign(Object.assign({}, buttonSecondaryHover), { backgroundColor: tokens.hoverTertiary }), '&:active': Object.assign(Object.assign({}, buttonSecondaryActive), { backgroundColor: tokens.pressedTertiary }) }),
        }, neutral: {
            '&:not(.Mui-disabled)': Object.assign(Object.assign(Object.assign({}, normalizedButton), buttonNeutral), { '&:hover': Object.assign(Object.assign({}, buttonNeutralHover), { backgroundColor: tokens.hoverTertiary }), '&.Mui-focusVisible': {
                    boxShadow: tokens.highlight,
                    border: tokens.inverseInteractive01,
                }, '&:active': Object.assign(Object.assign({}, buttonNeutralActive), { backgroundColor: tokens.pressedTertiary }) }),
        }, neutralInverse: {
            '&:not(.Mui-disabled)': Object.assign(Object.assign(Object.assign({}, normalizedButton), buttonNeutralInverse), { '&:hover': Object.assign({}, buttonNeutralInverseHover), '&.Mui-focusVisible': Object.assign({}, buttonNeutralInverseFocus), '&:active': Object.assign({}, buttonNeutralInverseActive) }),
        }, text: {
            '&:disabled': Object.assign(Object.assign({}, buttonDisabledTertiary), { minWidth: 80 }),
            '&:not(.Mui-disabled)': Object.assign(Object.assign(Object.assign({}, normalizedButton), buttonTertiary), { color: tokens.inverseInteractive01, '&:hover': Object.assign(Object.assign({}, buttonTertiaryHover), { backgroundColor: tokens.hoverTertiary }), '&.Mui-focusVisible': Object.assign(Object.assign({}, buttonTertiaryFocus), { boxShadow: tokens.highlight, border: `1px solid ${tokens.inverseInteractive01} !important` }), '&:active': Object.assign(Object.assign({}, buttonTertiaryActive), { backgroundColor: tokens.pressedTertiary }) }),
        }, tertiaryNeutral: {
            '&:not(.Mui-disabled)': Object.assign(Object.assign(Object.assign({}, normalizedButton), buttonTertiaryNeutral), { '&:hover': Object.assign(Object.assign({}, buttonTertiaryNeutralHover), { backgroundColor: tokens.hoverTertiary, border: `1px solid tranparent` }), '&.Mui-focusVisible': {
                    boxShadow: tokens.highlight,
                    border: tokens.inverseInteractive01,
                }, '&:active': Object.assign(Object.assign({}, buttonTertiaryNeutralActive), { backgroundColor: tokens.pressedTertiary, border: `1px solid tranparent` }) }),
        }, danger: {
            '&:not(.Mui-disabled)': Object.assign(Object.assign(Object.assign({}, normalizedButton), buttonDanger), { '&:hover': Object.assign({}, buttonDangerHover), '&.Mui-focusVisible': Object.assign({}, buttonDangerFocus), '&:active': Object.assign({}, buttonDangerActive) }),
        }, success: {
            '&:not(.Mui-disabled)': Object.assign(Object.assign(Object.assign({}, normalizedButton), buttonSuccess), { '&:hover': Object.assign({}, buttonSuccessHover), '&.Mui-focusVisible': Object.assign({}, buttonSuccessFocus), '&:active': Object.assign({}, buttonSuccessActive) }),
        } });
});
export const Button = forwardRef((_a, ref) => {
    var { className, loading, color = 'default', startIcon, iconButton, children, size = 'medium', variant = 'text', inverse = false } = _a, otherProps = __rest(_a, ["className", "loading", "color", "startIcon", "iconButton", "children", "size", "variant", "inverse"]);
    const classes = useStyles();
    const loadingSizes = {
        small: {
            size: 12,
            ml: 0.5,
            top: -1,
        },
        medium: {
            size: 14,
            ml: 0.25,
            top: 0,
        },
        large: {
            size: 16,
            ml: 0,
            top: 1,
        },
        xlarge: {
            size: 18,
            ml: 0,
            top: 2,
        },
    };
    const overrideClasses = [
        classes[variant],
        classes[color],
        color === 'default' && variant === 'text' ? classes['tertiaryNeutral'] : {},
        color === 'default' && variant === 'outlined' ? classes['neutral'] : {},
        color === 'default' && !!inverse ? classes['neutralInverse'] : {},
    ];
    return (React.createElement(MuiButton, Object.assign({ className: clsx(classes[size], ...overrideClasses, iconButton && classes.iconButton, className), focusVisibleClassName: classes.focusVisible, disableElevation: true, disableTouchRipple: true, startIcon: loading ? (React.createElement(Box, { ml: loadingSizes[size].ml, position: "relative", top: loadingSizes[size].top },
            React.createElement(CircularProgress, { color: "inherit", disableShrink: true, size: loadingSizes[size].size }))) : (startIcon), color: color === 'danger' || color === 'success' ? 'default' : color, innerRef: ref, variant: variant }, otherProps), children));
});
